import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import Select from 'react-select';
import axios from 'axios'
import { BASEURL } from "../Config"
import Footer from "../components/Footer";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import swal from 'sweetalert';
import { State, City } from 'country-state-city';
import CustomPagination from "../components/common/CustomPagination";
import FilterModal from "../components/common/FilterModal";
import ActionBar from "../components/common/ActionBar";

const SalesManagers = () => {
    const history = useHistory();
    const [currentPage, setCurrentPage] = useState(0);
    const [offset, setOffset] = useState(0);
    const [slice, setSlice] = useState([]);
    const [perPage, setPerPage] = useState(5);
    const [pageCount, setPageCount] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [noResults, setNoResults] = useState(false);
    const [selectedStates, setSelectedStates] = useState([]);
    const [selectedCities, setSelectedCities] = useState([]);
    const [selectedSalesManager, setSelectedSalesManager] = useState(null);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [allSalesManagers, setAllSalesManagers] = useState([]);
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [visiblePasswords, setVisiblePasswords] = useState({});

    useEffect(() => {
        const statesData = State.getStatesOfCountry('IN').map(state => ({
            label: state.name,
            value: state.isoCode
        }));
        setStates(statesData);
        
        // Fetch all sales managers for the dropdown
        axios.get(`${BASEURL}/api/v1/salesManager?offset=0&limit=1000&fields=firstName,lastName,uuid`)
            .then(res => {
                if (res.status === 200) {
                    const managersOptions = res.data.salesManagers.map(sm => ({
                        label: `${sm.firstName} ${sm.lastName}`,
                        value: sm.uuid
                    }));
                    setAllSalesManagers(managersOptions);
                }
            })
            .catch(err => console.log('Error fetching all sales managers', err));
    }, []);

    useEffect(() => {
        if (selectedStates.length > 0) {
            let allCities = [];
            selectedStates.forEach(state => {
                const citiesData = City.getCitiesOfState('IN', state.value).map(city => ({
                    label: city.name,
                    value: city.name
                }));
                allCities = [...allCities, ...citiesData];
            });
            setCities(allCities);
        } else {
            setCities([]);
            setSelectedCities([]);
        }
    }, [selectedStates]);

    useEffect(() => {
        fetchSalesManagers();
    }, [offset, perPage]);

    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            if (searchTerm !== undefined) {
                setOffset(0);
                setCurrentPage(0);
                fetchSalesManagers();
            }
        }, 500);

        return () => clearTimeout(debounceTimer);
    }, [searchTerm]);

    const fetchSalesManagers = () => {
        let url = `${BASEURL}/api/v1/salesManager?offset=${offset}&limit=${perPage}`;
        if (searchTerm) {
            url += `&search=${searchTerm}`;
        }
        if (selectedStates.length > 0) {
            const stateNames = selectedStates.map(s => s.label).join(',');
            url += `&states=${stateNames}`;
        }
        if (selectedCities.length > 0) {
            const cityNames = selectedCities.map(c => c.label).join(',');
            url += `&cities=${cityNames}`;
        }
        if (selectedSalesManager) {
            url += `&salesManagerId=${selectedSalesManager.value}`;
        }

        axios.get(url)
            .then(res => {
                if (res.status === 200) {
                    setSlice(res.data.salesManagers);
                    setPageCount(Math.ceil(res.data.total / perPage));
                    setTotalCount(res.data.total);
                    setNoResults(res.data.salesManagers.length === 0);
                }
            })
            .catch((err) => {
                console.log(err.response?.data);
                swal('Error in Fetching Sales Managers', err.response?.data, 'error');
                console.log('Fetching Sales Managers Error', err);
            });
    };

    const handlePerPageChange = (e) => {
        const newPerPage = parseInt(e.target.value);
        setPerPage(newPerPage);
        setOffset(0);
        setCurrentPage(0);
    };

    const handlePageClick = (e) => {
        const selectedPage = e.selected;
        const newOffset = selectedPage * perPage;
        setCurrentPage(selectedPage);
        setOffset(newOffset);
    };

    const handleSearch = () => {
        setOffset(0);
        setCurrentPage(0);
        fetchSalesManagers();
    };

    const handleShowAll = () => {
        setSearchTerm('');
        setSelectedStates([]);
        setSelectedCities([]);
        setSelectedSalesManager(null);
        setNoResults(false);
        setCurrentPage(0);
        if (offset !== 0) {
            setOffset(0);
        } else {
             axios.get(`${BASEURL}/api/v1/salesManager?offset=0&limit=${perPage}`)
            .then(res => {
                if (res.status === 200) {
                    setSlice(res.data.salesManagers);
                    setPageCount(Math.ceil(res.data.total / perPage));
                    setNoResults(res.data.salesManagers.length === 0);
                }
            })
            .catch((err) => {
                console.log(err.response?.data);
                swal('Error in Fetching Sales Managers', err.response?.data, 'error');
            });
        }
    };

    const togglePasswordVisibility = (uuid) => {
        setVisiblePasswords(prev => ({
            ...prev,
            [uuid]: !prev[uuid]
        }));
    };

    return (
        <div id="wrapper">
            <Sidebar act="salesManagers" />
            <div className="content-page">
                <div className="content">
                    <Topbar act="Sales Managers" breadcrumbItems={['Sales App', 'Sales Managers']} />
                    <div className="sales-manager-container">
                        <ActionBar 
                            addLink="/addSalesManager"
                            addLabel="Add Sales Manager"
                            onSearchChange={(e) => setSearchTerm(e.target.value)}
                            searchTerm={searchTerm}
                            showFilter={true}
                            onFilterClick={() => setShowFilterModal(true)}
                            placeholder="Search managers..."
                        >
                            <FilterModal 
                                show={showFilterModal}
                                onClose={() => setShowFilterModal(false)}
                                states={states}
                                cities={cities}
                                salesManagers={allSalesManagers}
                                selectedStates={selectedStates}
                                selectedCities={selectedCities}
                                selectedSalesManager={selectedSalesManager}
                                onStateChange={setSelectedStates}
                                onCityChange={setSelectedCities}
                                onSalesManagerChange={setSelectedSalesManager}
                                onApply={() => {
                                    handleSearch();
                                    setShowFilterModal(false);
                                }}
                                onReset={handleShowAll}
                            />
                        </ActionBar>

                        <div className="table-card">
                            <div style={{ overflowX: 'auto' }}>
                                <table className="table mb-0">
                                    <thead>
                                        <tr>
                                            <th>SNo</th>
                                            <th>Name</th>
                                            <th>Phone Number</th>
                                            <th>State</th>
                                            <th>City</th>
                                            <th>Username</th>
                                            <th>Password</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    {noResults ?
                                        <tbody>
                                            <tr>
                                                <td colSpan={8} style={{ textAlign: 'center' }}>
                                                    No Results Found
                                                </td>
                                            </tr>
                                        </tbody>
                                        :
                                        <tbody>
                                            {slice.map((data, idx) => (
                                                <tr 
                                                    key={data.uuid} 
                                                    onClick={() => history.push('/viewSalesManager/' + data.uuid)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <td>{currentPage * perPage + idx + 1}</td>
                                                    <td>
                                                        <div className="user-name-cell">
                                                            <div className="user-avatar">
                                                                <i className="mdi mdi-account" style={{ fontSize: '20px' }}></i>
                                                            </div>
                                                            <span className="user-name-text">
                                                                {data.firstName + ' ' + data.lastName}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td>{data.phoneNo}</td>
                                                    <td>{Array.isArray(data.states) ? data.states.map(s => s.label).join(', ') : data.states}</td>
                                                    <td>{Array.isArray(data.cities) ? data.cities.map(c => c.label).join(', ') : data.cities}</td>
                                                    <td>{data.username}</td>
                                                    <td>
                                                        <div className="password-cell">
                                                            <span className="password-dots">
                                                                {visiblePasswords[data.uuid] ? data.password : '••••••••'}
                                                            </span>
                                                            <button 
                                                                className="btn btn-sm btn-password-toggle" 
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    togglePasswordVisibility(data.uuid);
                                                                }}
                                                                title={visiblePasswords[data.uuid] ? "Hide password" : "Show password"}
                                                            >
                                                                <i className={`mdi mdi-eye${visiblePasswords[data.uuid] ? '-off' : ''}-outline`}></i>
                                                            </button>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="action-buttons">
                                                            <button 
                                                                className="btn btn-sm btn-action"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    history.push('/editSalesManager/' + data.uuid);
                                                                }}
                                                                title="Edit"
                                                            >
                                                                <i className="mdi mdi-pencil-outline"></i>
                                                            </button>
                                                            <button 
                                                                className="btn btn-sm btn-action"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    history.push('/viewSalesManager/' + data.uuid);
                                                                }}
                                                                title="View"
                                                            >
                                                                <i className="mdi mdi-eye-outline"></i>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    }
                                </table>
                            </div>

                            <CustomPagination 
                                offset={offset}
                                perPage={perPage}
                                totalCount={totalCount}
                                pageCount={pageCount}
                                currentPage={currentPage}
                                onPageChange={handlePageClick}
                                onPerPageChange={handlePerPageChange}
                                sliceLength={slice.length}
                                itemLabel="managers"
                            />
                        </div>
                    </div>
                    <Footer />
                </div>
            </div>
        </div>
    );
}

export default SalesManagers;
